import { LayoutGrid, List, Map as MapIcon, SlidersHorizontal } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { useApi } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtNum } from "../lib/format";
import { PageHero, Loading } from "../components/Common";
import { Sel } from "../components/Fields";
import FilterPanel from "../components/FilterPanel";
import PropertyCard from "../components/PropertyCard";
import { PropertiesMap } from "../components/Maps";
import RecentlyViewed from "../components/RecentlyViewed";
import { NLSearch, FilterChips } from "../components/NLSearch";
import { PreferenceQuiz } from "../components/Recommendations";
import { SaveSearch } from "./Account";
import Seo from "../components/Seo";

const VIEWS = [["grid", LayoutGrid], ["list", List], ["map", MapIcon]];

function Pager({ page, pages, go }) {
  const { t, lang } = useI18n();
  if (pages <= 1) return null;
  return (
    <nav aria-label="Pagination" data-testid="listing-pagination" className="mt-12 flex items-center justify-center gap-2">
      <button type="button" data-testid="pagination-prev" disabled={page <= 1} onClick={() => go(page - 1)} className="chip disabled:opacity-30">{t("listing.prev")}</button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
        <button key={n} type="button" data-testid={`pagination-page-${n}`} aria-current={n === page ? "page" : undefined} onClick={() => go(n)} className={`chip !px-3.5 ${n === page ? "chip-on" : ""}`}>{fmtNum(n, lang)}</button>
      ))}
      <button type="button" data-testid="pagination-next" disabled={page >= pages} onClick={() => go(page + 1)} className="chip disabled:opacity-30">{t("listing.next")}</button>
    </nav>
  );
}

export default function Listings({ kind = "all", preset = {} }) {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "grid";
  const update = (obj) => {
    const p = new URLSearchParams(params);
    Object.entries(obj).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (!("page" in obj) && !("view" in obj) && !("sort" in obj)) p.delete("page");
    setParams(p);
  };
  const clear = () => setParams(view !== "grid" ? { view } : {});
  const q = new URLSearchParams(params);
  q.delete("view");
  Object.entries(preset).forEach(([k, v]) => q.set(k, v));
  const mq = new URLSearchParams(q);
  mq.delete("page");
  const list = useApi(view === "map" ? null : `/properties?${q.toString()}`);
  const markers = useApi(view === "map" ? `/properties/markers?${mq.toString()}` : null);
  const total = view === "map" ? markers.data?.length : list.data?.total;
  const panel = <FilterPanel params={params} update={update} clear={clear} preset={preset} />;

  return (
    <div data-testid={`listings-page-${kind}`}>
      <Seo title={t(`listing.titles.${kind}`)} description={t(`listing.subs.${kind}`)} />
      <PageHero eyebrow={t("nav.properties")} title={t(`listing.titles.${kind}`)} sub={t(`listing.subs.${kind}`)} />
      <div className="container-lux grid gap-10 py-12 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block" aria-label={t("listing.filters")}><div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pe-2">{panel}</div></aside>
        <div>
          <NLSearch className="mb-4" testid="listing-nl-search" />
          <div className="mb-4 flex flex-wrap gap-2"><SaveSearch /><PreferenceQuiz /></div>
          <FilterChips />
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-fg/10 pb-5">
            <p data-testid="listing-results-count" className="text-sm text-fg/60"><span className="font-display text-2xl text-fg">{fmtNum(total || 0, lang)}</span> {t("listing.results")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Sheet>
                <SheetTrigger data-testid="mobile-filters-button" className="chip lg:hidden"><SlidersHorizontal className="h-4 w-4" />{t("listing.filters")}</SheetTrigger>
                <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto border-cta/20 bg-canvas text-fg">
                  <SheetTitle className="eyebrow mb-6">{t("listing.filters")}</SheetTitle>{panel}
                </SheetContent>
              </Sheet>
              {view !== "map" && (
                <div className="w-52"><Sel noAny testid="listing-sort-select" label={t("listing.sort")} value={params.get("sort") || "newest"} onChange={(v) => update({ sort: v === "newest" ? "" : v })}
                  options={["newest", "price_asc", "price_desc", "size_desc", "size_asc"].map((s) => [s, t(`listing.sorts.${s}`)])} /></div>
              )}
              <div className="flex rounded-full border border-fg/15 p-1" role="group">
                {VIEWS.map(([v, Icon]) => (
                  <button key={v} type="button" data-testid={`view-toggle-${v}`} aria-pressed={view === v} aria-label={t(`listing.${v}`)} onClick={() => update({ view: v === "grid" ? "" : v })}
                    className={`rounded-full p-2 transition-colors ${view === v ? "bg-cta text-canvas" : "text-fg/60 hover:text-cta"}`}><Icon className="h-4 w-4" /></button>
                ))}
              </div>
            </div>
          </div>
          {view === "map" ? (
            markers.loading ? <Loading /> : <PropertiesMap items={markers.data || []} />
          ) : list.loading && !list.data ? <Loading /> : list.data?.items.length ? (
            <>
              <div data-testid="listing-results" className={view === "list" ? "flex flex-col gap-6" : "grid gap-6 sm:grid-cols-2 xl:grid-cols-3"}>
                {list.data.items.map((p) => (<PropertyCard key={p.id} p={p} layout={view} />))}
              </div>
              <Pager page={list.data.page} pages={list.data.pages} go={(n) => { update({ page: n > 1 ? String(n) : "" }); window.scrollTo({ top: 300, behavior: "smooth" }); }} />
            </>
          ) : (
            <div data-testid="listing-no-results" className="glass rounded-sm p-12 text-center">
              <p className="text-fg/60">{t("listing.noResults")}</p>
              <button type="button" onClick={clear} data-testid="no-results-clear" className="btn-ghost mt-6">{t("listing.clear")}</button>
            </div>
          )}
        </div>
      </div>
      <RecentlyViewed />
    </div>
  );
}
